// File: components/RepliesPage/ReplyCards/SpamCard.tsx
import { useState } from "react";
import { X } from "@/components/logos/X";
import {
  UserCircle,
  MapPin,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle,
  MessageSquare,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/api";
import TwitterMediaGrid from "@/components/twitter/TwitterMedia";
import { formatDate, formatNumber } from "@/components/twitter/twitterUtils";

/**
 * Deep parsing function to extract text from complex nested structures
 */
function parseNestedTweet(obj: unknown): string {
  // If it's a string that looks like JSON, try to parse it
  if (typeof obj === "string" && (obj.startsWith("{") || obj.startsWith("["))) {
    try {
      return parseNestedTweet(JSON.parse(obj));
    } catch (e) {
      // Not valid JSON, return the string
      console.debug("Failed to parse JSON:", e);
      return obj;
    }
  }

  // If it's already an object or array
  if (typeof obj === "object" && obj !== null) {
    // If it has a 'text' property, use that
    if ("text" in obj && obj.text) {
      // Check if the text property is itself an object/JSON
      if (typeof obj.text === "object") {
        return parseNestedTweet(obj.text);
      }
      return obj.text as string;
    }

    // Try common properties that might contain the main content
    const possibleTextKeys = [
      "content",
      "message",
      "tweet",
      "body",
      "value",
    ] as const;
    for (const key of possibleTextKeys) {
      if (obj[key as keyof typeof obj]) {
        return parseNestedTweet(obj[key as keyof typeof obj]);
      }
    }

    // For arrays, try to concatenate all items
    if (Array.isArray(obj)) {
      const texts = obj.map((item) => parseNestedTweet(item)).filter(Boolean);
      return texts.join("\n");
    }

    // If no direct text property, stringify the object but make it look nice
    return JSON.stringify(obj, null, 2);
  }

  // For primitives, just return as string
  return String(obj || "");
}

/**
 * Process spam reply data, handling complex nested JSON structures
 */
function processSpamData(reply: any) {
  if (!reply)
    return {
      createdAt: "",
      tweetText: "",
      username: "unknown",
      displayName: "Unknown User",
      profileImage: null,
      timestamp: "",
      location: "",
      joinedDate: "",
      followersCount: 0,
      verified: false,
      mediaUrls: [],
      threadContext: [],
      tweetId: "",
      reason: "No reason provided",
      riskFactors: [],
      confidence: 0,
    };
  console.log(reply);
  try {
    // Get the original message, handle both object and string JSON
    let originalMessage = reply.original_message;
    if (
      typeof originalMessage === "string" &&
      originalMessage.startsWith("{")
    ) {
      try {
        originalMessage = JSON.parse(originalMessage);
      } catch (e) {
        console.debug("Failed to parse JSON:", e);
        // Keep as string if parsing fails
      }
    }

    // Get user details
    const userDetails = originalMessage?.user_details || {};
    const metadata = reply.metadata || {};

    // Extract text using our improved parsing function
    let tweetText = originalMessage?.text || "";

    // Handle the case where the originalMessage.text is itself a complex structure
    tweetText = parseNestedTweet(tweetText);

    // Clean up the text (replace escaped characters, etc.)
    if (typeof tweetText === "string") {
      tweetText = tweetText
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\n")
        .replace(/\\t/g, "  ")
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'");
    }

    // Extract IDs and usernames safely
    const username =
      userDetails.username ||
      originalMessage?.sender_id ||
      originalMessage?.author_id ||
      originalMessage?.username ||
      "unknown";

    // Build the processed data object
    return {
      createdAt: reply.created_at || "",
      tweetText,
      username,
      displayName: userDetails.name || username,
      profileImage: userDetails.profile_image_url,
      timestamp:
        originalMessage?.timestamp ||
        originalMessage?.created_at ||
        reply.created_at ||
        "",
      location: userDetails.location || "",
      joinedDate: userDetails.created_at || "",
      followersCount: Number(userDetails.followers_count || 0),
      verified: Boolean(userDetails.verified || false),
      mediaUrls: Array.isArray(reply.media_urls)
        ? reply.media_urls
        : Array.isArray(originalMessage?.media_urls)
          ? originalMessage.media_urls
          : [],
      threadContext: Array.isArray(reply.thread_context)
        ? reply.thread_context
        : Array.isArray(originalMessage?.conversation)
          ? originalMessage.conversation
          : [],
      tweetId: originalMessage?.id || "",
      reason: metadata.reason || reply.reason || "No reason provided",
      riskFactors: Array.isArray(metadata.risk_factors)
        ? metadata.risk_factors
        : [],
      confidence: metadata.confidence || 0.95,
    };
  } catch (error) {
    console.error("Error processing spam data:", error);
    return {
      createdAt: "",
      tweetText: "Error processing tweet content",
      username: "unknown",
      displayName: "Unknown User",
      profileImage: null,
      timestamp: "",
      location: "",
      joinedDate: "",
      followersCount: 0,
      verified: false,
      mediaUrls: [],
      threadContext: [],
      tweetId: "",
      reason: "Error processing spam data",
      riskFactors: [],
      confidence: 0,
    };
  }
}

const SpamCard = ({ reply, personaId }: any) => {
  // State for managing actions
  const [isUnmarkingSpam, setIsUnmarkingSpam] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Process the data with our enhanced processor
  const processedData = useMemo(() => processSpamData(reply), [reply]);

  // Format the tweet text for display with proper handling of newlines
  const formattedTweetText = useMemo(() => {
    if (!processedData.tweetText) return null;

    // Split by newlines and render as paragraphs
    if (typeof processedData.tweetText === "string") {
      return processedData.tweetText.split(/\n/).map((line, i) =>
        line.trim() ? (
          <p key={i} className="mb-1 text-sm">
            {line}
          </p>
        ) : (
          <br key={i} />
        ),
      );
    }

    // Fallback for non-string content
    return <p>{String(processedData.tweetText)}</p>;
  }, [processedData.tweetText]);

  // Open tweet in new tab
  const openTweetInNewTab = () => {
    if (processedData.tweetId && processedData.username) {
      window.open(
        `https://twitter.com/${processedData.username}/status/${processedData.tweetId}`,
        "_blank",
      );
    }
  };

  // Handle unmark spam action
  const handleUnmarkSpam = async (shouldReply = false) => {
    try {
      // Set appropriate loading state
      if (shouldReply) {
        setIsReplying(true);
      } else {
        setIsUnmarkingSpam(true);
      }

      // Call API to unmark spam
      await apiClient.post("/api/twitter-replies/unmark-spam", {
        persona_id: personaId,
        doc_id: reply.doc_id,
        should_reply: shouldReply,
      });

      // Show success message
      toast({
        description: shouldReply
          ? "Message unmarked as spam and reply sent successfully"
          : "Message unmarked as spam successfully",
      });

      // Refresh the list
      queryClient.invalidateQueries({
        queryKey: ["spam-replies", personaId],
      });
    } catch (error: any) {
      console.error("Error unmarking spam:", error);
      toast({
        description: `Failed to unmark spam: ${error.message || "Unknown error"}`,
        variant: "destructive",
      });
    } finally {
      setIsUnmarkingSpam(false);
      setIsReplying(false);
    }
  };

  return (
    <div className="bg-background border rounded-2xl">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 border-b border-border">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-red-500/20 text-red-500">
              Spam Detected
            </Badge>
            <span className="text-sm text-muted-foreground">
              {formatDate(processedData.createdAt)}
            </span>
          </div>
          <X width={16} height={16} fill="white" />
        </div>

        {/* Original message */}
        <div className="border rounded-xl p-3 bg-gray-100 text-black">
          <div className="flex items-start gap-2">
            {/* User Avatar */}
            <div className="flex-shrink-0">
              {processedData.profileImage ? (
                <a
                  href={`https://twitter.com/${processedData.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-90 transition-opacity"
                >
                  <img
                    src={processedData.profileImage}
                    alt={`${processedData.displayName}'s avatar`}
                    className="w-10 h-10 rounded-full"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = "none";

                      // Create fallback icon container
                      const div = document.createElement("div");
                      div.className =
                        "w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center";

                      // Create icon inside container
                      const icon = document.createElement("div");
                      icon.innerHTML =
                        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-gray-500"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>';
                      div.appendChild(icon);

                      // Replace img with div
                      e.currentTarget.parentNode?.replaceChild(
                        div,
                        e.currentTarget,
                      );
                    }}
                  />
                </a>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-gray-500" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {/* User Info Header */}
              <div className="flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col">
                    <div className="font-bold text-black line-clamp-1">
                      {processedData.displayName}
                      {processedData.verified && (
                        <span className="ml-1 inline-flex items-center justify-center bg-blue-500 text-white rounded-full w-4 h-4 text-sm">
                          ✓
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-sm">
                      @{processedData.username}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 flex-shrink-0">
                    {formatDate(processedData.timestamp)}
                  </span>
                </div>

                {/* User meta information */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mt-1 mb-2">
                  {processedData.location && (
                    <div className="flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span>{processedData.location}</span>
                    </div>
                  )}
                  {processedData.joinedDate && (
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        Joined{" "}
                        {formatDate(processedData.joinedDate, {
                          month: "long",
                          year: "numeric",
                          day: undefined,
                          hour: undefined,
                          minute: undefined,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    <span>
                      {formatNumber(processedData.followersCount)} followers
                    </span>
                  </div>
                </div>
              </div>

              {/* Tweet content - Enhanced display */}
              <div className="mt-1 text-black whitespace-pre-wrap">
                {formattedTweetText}
              </div>
            </div>
          </div>

          {/* Using our shared TwitterMediaGrid component */}
          <TwitterMediaGrid urls={processedData.mediaUrls} />

          {/* Thread context */}
          {processedData.threadContext &&
            processedData.threadContext.length > 1 && (
              <div className="mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openTweetInNewTab}
                  className="text-gray-600 hover:text-white hover:bg-gray-800"
                >
                  Show full conversation in new tab
                </Button>
              </div>
            )}
        </div>
      </div>

      {/* Spam Information Section */}
      <div className="px-6 py-4">
        <div className="flex items-start gap-2 mb-3">
          <AlertCircle className="h-5 w-5 text-red-500 mt-1" />
          <div className="flex-1">
            <div className="font-medium">Spam detected:</div>
            <p className="text-sm text-muted-foreground">
              {processedData.reason}
            </p>

            {/* Show risk factors */}
            {processedData.riskFactors &&
              processedData.riskFactors.length > 0 && (
                <div className="mt-2">
                  <div className="font-medium">Risk factors:</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {processedData.riskFactors.map(
                      (factor: any, index: number) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-red-100 text-red-800"
                        >
                          {factor}
                        </Badge>
                      ),
                    )}
                  </div>
                </div>
              )}

            {/* Confidence level */}
            {processedData.confidence > 0 && (
              <div className="mt-2">
                <div className="font-medium">Detection confidence:</div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                  <div
                    className="bg-red-600 h-2.5 rounded-full"
                    style={{
                      width: `${Math.min(processedData.confidence * 100, 100)}%`,
                    }}
                  ></div>
                </div>
                <div className="text-xs text-right mt-1 text-muted-foreground">
                  {Math.round(processedData.confidence * 100)}%
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-3 border-t flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => handleUnmarkSpam(false)}
          disabled={isUnmarkingSpam || isReplying}
        >
          {isUnmarkingSpam ? (
            <span className="flex items-center">
              <span className="animate-spin mr-2">⋯</span>
              Unmarking...
            </span>
          ) : (
            <span className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              Not Spam
            </span>
          )}
        </Button>
        <Button
          variant="default"
          onClick={() => handleUnmarkSpam(true)}
          disabled={isUnmarkingSpam || isReplying}
        >
          {isReplying ? (
            <span className="flex items-center">
              <span className="animate-spin mr-2">⋯</span>
              Replying...
            </span>
          ) : (
            <span className="flex items-center">
              <MessageSquare className="w-4 h-4 mr-1" />
              Not Spam & Reply
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SpamCard;
